import { useState } from "react";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { Button, Label } from "@common/index";
import { Input } from "@common/Form/Input";
import type { CategoryResponse } from "@models/category";
import { ComponentColor } from "@common/types";

export const Category = () => {
    const { createCategory, getCategories } = useCategoryApi();
    const [name, setName] = useState("");
    const [parentId, setParentId] = useState("");
    const [categories, setCategories] = useState<CategoryResponse[]>([]);

    const handleCreate = async () => {
        if (!name.trim()) {
            return;
        }

        await createCategory({
            name: name.trim(),
            ...(parentId && { parentId: parseInt(parentId) }),
        });

        setName("");
        setParentId("");
    };

    const handleGetCategories = async () => {
        const response = await getCategories();
        setCategories(response);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl">
            <h2 className="text-xl font-bold mb-4">Create Category</h2>

            <div className="space-y-4 mb-6">
                <div>
                    <Label>Category Name *</Label>
                    <Input
                        name="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter category name"
                    />
                </div>

                <div>
                    <Label>Parent Category ID (Optional)</Label>
                    <Input
                        name="parentId"
                        type="number"
                        value={parentId}
                        onChange={(e) => setParentId(e.target.value)}
                        placeholder="Enter parent category ID"
                    />
                </div>

                <div className="flex gap-2">
                    <Button onClick={handleGetCategories} color={ComponentColor.Secondary}>
                        Get All Categories
                    </Button>
                    <Button onClick={handleCreate} color={ComponentColor.Primary}>
                        Create Category
                    </Button>
                </div>
            </div>

            {categories.length > 0 && (
                <div className="mt-6 border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Categories:</h3>
                    <div className="bg-gray-50 p-4 rounded text-left">
                        {(() => {
                            const flatten = (
                                cats: CategoryResponse[],
                                path: string[] = [],
                            ): Array<{ id: number; path: string }> => {
                                const result: Array<{
                                    id: number;
                                    path: string;
                                }> = [];
                                cats.forEach((cat) => {
                                    const currentPath = [...path, cat.name];
                                    result.push({
                                        id: cat.id,
                                        path: currentPath.join(" / "),
                                    });
                                    if (
                                        cat.children &&
                                        cat.children.length > 0
                                    ) {
                                        result.push(
                                            ...flatten(
                                                cat.children,
                                                currentPath,
                                            ),
                                        );
                                    }
                                });
                                return result;
                            };
                            return flatten(categories).map((item) => (
                                <div key={item.id} className="py-1 text-left">
                                    <span className="font-medium">
                                        ID: {item.id}
                                    </span>
                                    - {item.path}
                                </div>
                            ));
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};
